import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useTheme } from '../../contexts/ThemeContext'
import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'

/* ── Floating Element (generic) ─────────────────────────────── */
export const FloatingElement = ({ 
  children, 
  className = '',
  animation = 'float',
  delay = 0,
  ...props 
}) => {
  const animations = {
    float: {
      y: [0, -6, 0],
      transition: { duration: 4, repeat: Infinity, ease: "easeInOut", delay }
    },
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: { duration: 3, repeat: Infinity, ease: "easeInOut", delay }
    },
    rotate: {
      rotate: [0, 360],
      transition: { duration: 12, repeat: Infinity, ease: "linear", delay }
    },
  }

  return (
    <motion.div
      className={className}
      animate={animations[animation] || animations.float}
      {...props}
    >
      {children}
    </motion.div>
  )
}

/* ── Particle field (dots floating in background) ───────────── */
export const ParticleField = ({ count = 20, className = '' }) => {
  const { isDark } = useTheme()
  const [particles] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      dur: 3 + Math.random() * 5,
      delay: Math.random() * 4,
    }))
  )

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            backgroundColor: isDark ? 'rgba(20,184,166,0.35)' : 'rgba(13,148,136,0.45)',
          }}
          animate={{
            y: [0, -(10 + Math.random() * 20), 0],
            opacity: [0.2, 0.8, 0.2],
          }}
          transition={{
            duration: p.dur,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: p.delay,
          }}
        />
      ))}
    </div>
  )
}

/* ── Aurora gradient background ─────────────────────────────── */
export const AuroraBackground = ({ className = '' }) => {
  const { isDark } = useTheme()

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Primary aurora sweep */}
      <div
        className="absolute inset-0 animate-aurora"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, rgba(20,184,166,0.08) 0%, transparent 30%, rgba(139,92,246,0.06) 60%, transparent 100%)'
            : 'linear-gradient(135deg, rgba(13,148,136,0.12) 0%, transparent 30%, rgba(124,58,237,0.08) 60%, transparent 100%)',
          backgroundSize: '400% 400%',
        }}
      />
      {/* Secondary cross sweep */}
      <div
        className="absolute inset-0 animate-aurora"
        style={{
          background: isDark
            ? 'linear-gradient(225deg, transparent 0%, rgba(20,184,166,0.05) 40%, transparent 70%, rgba(59,130,246,0.04) 100%)'
            : 'linear-gradient(225deg, transparent 0%, rgba(13,148,136,0.08) 40%, transparent 70%, rgba(59,130,246,0.06) 100%)',
          backgroundSize: '400% 400%',
          animationDelay: '-4s',
        }}
      />
    </div>
  )
}

/* ── Perspective grid (3D floor plane) ──────────────────────── */
export const PerspectiveGrid = ({ className = '' }) => {
  const { isDark } = useTheme()
  const color = isDark ? 'rgba(20,184,166,0.06)' : 'rgba(13,148,136,0.08)'

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} style={{ perspective: '600px' }}>
      <div
        className="absolute w-[200%] h-[200%] -left-1/2 top-1/2"
        style={{
          backgroundImage: `
            linear-gradient(${color} 1px, transparent 1px),
            linear-gradient(90deg, ${color} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          transform: 'rotateX(55deg)',
          animation: 'grid-float 8s ease-in-out infinite',
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)',
        }}
      />
    </div>
  )
}

/* ── Orbiting dots ──────────────────────────────────────────── */
export const OrbitDots = ({ count = 3, radius = 80, className = '' }) => {
  const { isDark } = useTheme()

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="absolute left-1/2 top-1/2"
          style={{
            '--orbit-radius': `${radius + i * 30}px`,
            animation: `orbit ${8 + i * 4}s linear infinite`,
            animationDelay: `${i * -2}s`,
          }}
        >
          <div
            className="rounded-full"
            style={{
              width: 3 + i,
              height: 3 + i,
              backgroundColor: isDark ? 'rgba(20,184,166,0.5)' : 'rgba(13,148,136,0.6)',
              boxShadow: isDark ? '0 0 8px rgba(20,184,166,0.4)' : '0 0 8px rgba(13,148,136,0.4)',
            }}
          />
        </div>
      ))}
    </div>
  )
}

/* ── Mouse spotlight (follows cursor) ──────────────────────── */
export const MouseSpotlight = ({ className = '' }) => {
  const { isDark } = useTheme()
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const springX = useSpring(mouseX, { stiffness: 80, damping: 30 })
  const springY = useSpring(mouseY, { stiffness: 80, damping: 30 })

  useEffect(() => {
    const handleMove = (e) => {
      mouseX.set(e.clientX)
      mouseY.set(e.clientY)
    }
    window.addEventListener('mousemove', handleMove)
    return () => window.removeEventListener('mousemove', handleMove)
  }, [mouseX, mouseY])

  return (
    <motion.div
      className={`fixed inset-0 pointer-events-none z-0 ${className}`}
      style={{
        background: useTransform(
          [springX, springY],
          ([x, y]) =>
            `radial-gradient(600px circle at ${x}px ${y}px, ${
              isDark ? 'rgba(20,184,166,0.04)' : 'rgba(20,184,166,0.03)'
            }, transparent 60%)`
        ),
      }}
    />
  )
}

/* ── Scanline overlay ───────────────────────────────────────── */
export const Scanline = ({ className = '' }) => (
  <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
    <div
      className="absolute left-0 w-full h-[2px] opacity-[0.03]"
      style={{
        background: 'linear-gradient(90deg, transparent, rgba(20,184,166,0.6), transparent)',
        animation: 'scanline 6s linear infinite',
      }}
    />
  </div>
)

FloatingElement.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  animation: PropTypes.oneOf(['float', 'pulse', 'rotate']),
  delay: PropTypes.number,
}

ParticleField.propTypes = { count: PropTypes.number, className: PropTypes.string }
AuroraBackground.propTypes = { className: PropTypes.string }
PerspectiveGrid.propTypes = { className: PropTypes.string }
OrbitDots.propTypes = { count: PropTypes.number, radius: PropTypes.number, className: PropTypes.string }
MouseSpotlight.propTypes = { className: PropTypes.string }
Scanline.propTypes = { className: PropTypes.string }